import { useCallback, useEffect, useRef, useState } from 'react';
import { WindowContent, WindowHeader } from 'react95';
import styled from 'styled-components';
import DraggableComponent from '../DraggableComponent';
import WindowComponent from '../WindowComponent';
import WindowButton from '../buttons/WindowButton';
import './doomwindow.scss';

const Wrapper = styled.div`
  position: absolute;
  background: transparent;
`;

// Player e emulador (js-dos v8) servidos do próprio site: public/js-dos/.
// O jogo é o DOOM shareware v1.9 (DOOM1.WAD), redistribuível gratuitamente.
const JSDOS_BASE = `${process.env.PUBLIC_URL}/js-dos`;
const DOOM_BUNDLE_URL = `${process.env.PUBLIC_URL}/doom1.jsdos`;

// Singleton: o script/css do js-dos só são injetados na primeira vez
// que a janela do DOOM abre — custo zero para quem não joga.
let jsDosLoader = null;

const loadJsDos = () => {
  if (window.Dos) return Promise.resolve(window.Dos);
  if (!jsDosLoader) {
    jsDosLoader = new Promise((resolve, reject) => {
      if (!document.querySelector('link[data-jsdos]')) {
        const css = document.createElement('link');
        css.rel = 'stylesheet';
        css.href = `${JSDOS_BASE}/js-dos.css`;
        css.dataset.jsdos = 'true';
        document.head.appendChild(css);
      }
      const script = document.createElement('script');
      script.src = `${JSDOS_BASE}/js-dos.js`;
      script.async = true;
      script.onload = () => {
        if (window.Dos) resolve(window.Dos);
        else {
          jsDosLoader = null;
          script.remove();
          reject(new Error('js-dos carregado, mas window.Dos não existe'));
        }
      };
      script.onerror = () => {
        jsDosLoader = null;
        script.remove();
        reject(new Error('Falha ao carregar o js-dos'));
      };
      document.head.appendChild(script);
    });
  }
  return jsDosLoader;
};

const DoomWindow = ({ displayTasks, displayingTask, setActiveTask, activeTask, indexingTasks, taskIndices, tasksVisibility, setTasksVisibility, setTaskSwitiching }) => {
  const task = 'doom';
  const [isDraggable, setIsDraggable] = useState(true);
  const initialPosition = window.innerWidth > 720 ? { x: (window.innerWidth - 680) / 2, y: 30 } : { x: window.innerWidth * 0.02, y: 10 };
  const playerRef = useRef(null);
  const dosRef = useRef(null);
  const stopPromiseRef = useRef(Promise.resolve());
  const [status, setStatus] = useState('idle');
  const isOpen = displayTasks.has(task);

  const stopDos = useCallback(() => {
    const dos = dosRef.current;
    dosRef.current = null;
    if (!dos) return;
    const host = playerRef.current;
    stopPromiseRef.current = (async () => {
      try {
        await dos.stop();
      } catch (error) {
        console.warn('O emulador não concluiu o encerramento.', error);
      } finally {
        host?.replaceChildren();
      }
    })();
  }, []);

  useEffect(() => {
    if (!isOpen) {
      stopDos();
      setStatus('idle');
      return;
    }
    let cancelled = false;
    setStatus('loading');
    Promise.all([loadJsDos(), stopPromiseRef.current])
      .then(([Dos]) => {
        if (cancelled || dosRef.current || !playerRef.current) return;
        dosRef.current = Dos(playerRef.current, {
          url: DOOM_BUNDLE_URL,
          pathPrefix: `${JSDOS_BASE}/emulators/`,
          autoStart: true,
          noCloud: true,
          theme: 'dark',
          workerThread: true,
          mouseCapture: true,
          autoSave: false,
          onEvent: (event) => {
            if (!cancelled && event === 'ci-ready') {
              setStatus('running');
            }
          },
        });
      })
      .catch(() => {
        if (!cancelled) {
          setStatus('error');
        }
      });
    return () => {
      cancelled = true;
      stopDos();
    };
  }, [isOpen, stopDos]);

  return (
    <DraggableComponent task={task} initialPosition={initialPosition} setActiveTask={setActiveTask} indexingTasks={indexingTasks} isDraggable={isDraggable} setIsDraggable={setIsDraggable}>
    <Wrapper className="drag-doom" style={{zIndex: taskIndices[task], display: isOpen ? 'block' : 'none', visibility: tasksVisibility[task]}}>
    <WindowComponent task={task} setActiveTask={setActiveTask} indexingTasks={indexingTasks}>
      <strong className="cursor"><WindowHeader active={activeTask === task} className='window-title'>
        <span>DOOM.EXE</span>
        <div className="buttons">
          <WindowButton purpose='minimise' tasksVisibility={tasksVisibility} task={task} setTasksVisibility={setTasksVisibility} setActiveTask={setActiveTask} setTaskSwitiching={setTaskSwitiching} setIsDraggable={setIsDraggable} />
          <WindowButton purpose='close' task={task} setActiveTask={setActiveTask} displayingTask={displayingTask} setIsDraggable={setIsDraggable} />
        </div>
      </WindowHeader></strong>
      <WindowContent className='window-content'>
        <div className="doom-player" ref={playerRef}></div>
        {status === 'loading' && <p className="doom-status">Carregando DOOM (shareware)…</p>}
        {status === 'error' && <p className="doom-status">Não foi possível iniciar o DOOM. Recarregue a página e tente de novo.</p>}
        <p className='doom-footer'>
          DOOM shareware © 1993 id Software — episódio 1 de distribuição gratuita. Emulação: js-dos/DOSBox (GPL-2.0). Clique na tela para capturar o mouse (Esc solta).
        </p>
      </WindowContent>
    </WindowComponent>
  </Wrapper>
  </DraggableComponent>
  );
};

export default DoomWindow;
